import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

const InstallContext = createContext(null);

export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches === true ||
    window.navigator.standalone === true ||
    document.referrer?.startsWith("android-app://") === true
  );
}

export function isIOS() {
  if (typeof navigator === "undefined") return false;
  return (
    /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

export function PWAInstallProvider({ children }) {
  const promptRef = useRef(null);
  const busyRef = useRef(false);
  const [canInstall, setCanInstall] = useState(false);
  const [installed, setInstalled] = useState(isStandalone);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onBeforeInstallPrompt = (event) => {
      // Preserve the browser's one-use native install event for the user click.
      event.preventDefault();
      promptRef.current = event;
      setCanInstall(true);
    };

    const onInstalled = () => {
      promptRef.current = null;
      setCanInstall(false);
      setInstalled(true);
    };

    const displayModeQuery = window.matchMedia?.("(display-mode: standalone)");
    const updateMode = () => setInstalled(isStandalone());

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);
    displayModeQuery?.addEventListener?.("change", updateMode);
    window.addEventListener("pageshow", updateMode);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      displayModeQuery?.removeEventListener?.("change", updateMode);
      window.removeEventListener("pageshow", updateMode);
    };
  }, []);

  const install = useCallback(async () => {
    if (isStandalone()) return "installed";
    if (busyRef.current) return "busy";

    const event = promptRef.current;
    if (!event) return "unavailable";

    busyRef.current = true;
    setBusy(true);
    promptRef.current = null;
    setCanInstall(false);

    try {
      // Run inside a user-initiated click handler; supported browsers decide
      // whether they can show a native installation prompt.
      await event.prompt();
      const response = await event.userChoice;
      return response?.outcome === "accepted" ? "accepted" : "dismissed";
    } catch (error) {
      console.error("Gwin: native installation prompt failed", error);
      return "error";
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, []);

  return (
    <InstallContext.Provider value={{ canInstall, installed, busy, install }}>
      {children}
    </InstallContext.Provider>
  );
}

export function usePWAInstall() {
  const context = useContext(InstallContext);
  if (!context) {
    throw new Error("usePWAInstall must be used inside PWAInstallProvider");
  }
  return context;
}
